import { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from '../shared/api';
import { formatMoney } from '../shared/formatting';
import { Icon } from './kycCharts';

/**
 * m.72 - evidencija otkupa iz Faros izvjestaja "Otkup po imenu i prezimenu za MUP".
 *
 * Dvije komponente:
 *   BuybackHistoryPanel - na predmetu, povijest stranke iz predmeta
 *   BuybackRegister     - pretraga cijele evidencije (ovlastenik)
 *
 * Do sada se ta povijest trazila rucno u Farosu. Povezane transakcije racuna
 * backend, jer Faros izvjestaj "Dojava povezanih transakcija" broji i storna
 * predujmova pa je neupotrebljiv.
 */

export type BuybackRow = {
    id: number;
    company_code: string;
    source_type: string;
    transaction_date: string | null;
    amount_eur: number | null;
    quantity_total: number | null;
    branch: string | null;
    branch_code: string | null;
    county: string | null;
    region: string | null;
    document_no: string | null;
    fiscal_no: string | null;
    name: string | null;
    address: string | null;
    source_file: string | null;
};

type Flag = { code: string; severity: 'high' | 'medium' | 'low'; label: string };
type LinkedGroup = { from: string; to: string; count: number; amount_eur: number; documents: string[] };
type Summary = {
    name: string | null; address: string | null; count: number; amount_eur: number;
    quantity_total: number; first_date: string | null; last_date: string | null;
    max_single_eur: number; rolling_12m_eur: number;
    branches: { branch: string; count: number }[];
    rows_shown: number; rows_truncated: boolean;
};

export type BuybackHistory = {
    available: boolean;
    found: boolean;
    reason?: string;
    masked?: boolean;
    rows: BuybackRow[];
    summary: Summary | null;
    by_year: { year: number; count: number; amount_eur: number; quantity: number }[];
    by_company: { company_code: string; source_type: string; count: number; amount_eur: number; quantity: number }[];
    linked_groups: LinkedGroup[];
    flags: Flag[];
    thresholds?: { cash_eur: number; low_eur: number; link_window_days: number };
};

const INK = '#23405f';
const card: React.CSSProperties = { background: '#fff', border: '1px solid #d8e2ec', borderRadius: 12, padding: 16, marginBottom: 14 };
const INP: React.CSSProperties = { padding: '8px 10px', border: '1px solid #ccd6e0', borderRadius: 8, fontSize: '0.88rem', width: '100%', boxSizing: 'border-box' };
const BTN: React.CSSProperties = { padding: '9px 15px', background: INK, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.86rem' };
const th: React.CSSProperties = { textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid #d8e2ec', color: '#4a6785', fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid #eef3f8', fontSize: '0.8rem', verticalAlign: 'top' };

const SEVERITY_COLOR: Record<string, string> = { high: '#bb1111', medium: '#8a6817', low: '#4a6785' };

const TYPE_LABEL: Record<string, string> = {
    buyback_lom: 'Otkup loma',
    buyback_investment: 'Otkup investicijskog',
    sale: 'Prodaja',
};

const money = (v: number | null | undefined): string => (v === null || v === undefined ? '-' : formatMoney(v, 'hr'));
const typeLabel = (t: string): string => TYPE_LABEL[t] ?? t;

function Stat({ label, value, note }: { label: string; value: string; note?: string }) {
    return (
        <div style={{ minWidth: 120 }}>
            <div style={{ fontSize: 10.5, color: '#8794a5', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em' }}>{label}</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: INK, marginTop: 2 }}>{value}</div>
            {note && <div style={{ fontSize: 10.5, color: '#8794a5', marginTop: 1 }}>{note}</div>}
        </div>
    );
}

function FlagList({ flags }: { flags: Flag[] }) {
    if (flags.length === 0) return null;
    return (
        <div style={{ marginTop: 12, display: 'grid', gap: 6 }}>
            {flags.map((f) => (
                <div
                    key={f.code}
                    style={{
                        display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', borderRadius: 8,
                        background: `${SEVERITY_COLOR[f.severity] ?? '#4a6785'}0f`,
                        border: `1px solid ${SEVERITY_COLOR[f.severity] ?? '#4a6785'}33`,
                    }}
                >
                    <span style={{ color: SEVERITY_COLOR[f.severity] ?? '#4a6785', flexShrink: 0, marginTop: 1 }}>
                        <Icon name={f.severity === 'high' ? 'alert' : 'flag'} size={14} />
                    </span>
                    <span style={{ fontSize: '0.82rem', color: '#33404f', lineHeight: 1.4 }}>{f.label}</span>
                </div>
            ))}
        </div>
    );
}

function RowTable({ rows, showName }: { rows: BuybackRow[]; showName: boolean }) {
    return (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={th}>Datum</th>
                        {showName && <th style={th}>Stranka</th>}
                        <th style={th}>Drustvo</th>
                        <th style={th}>Vrsta</th>
                        <th style={th}>Poslovnica</th>
                        <th style={th}>Dokument</th>
                        <th style={{ ...th, textAlign: 'right' }}>Kolicina</th>
                        <th style={{ ...th, textAlign: 'right' }}>Iznos</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id}>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.transaction_date ?? '-'}</td>
                            {showName && (
                                <td style={td}>
                                    <div style={{ fontWeight: 700 }}>{r.name ?? '-'}</div>
                                    {r.address && <div style={{ fontSize: '0.72rem', color: '#8794a5' }}>{r.address}</div>}
                                </td>
                            )}
                            <td style={td}>{r.company_code}</td>
                            <td style={td}>{typeLabel(r.source_type)}</td>
                            <td style={td}>
                                <div>{r.branch ?? '-'}</div>
                                {r.county && <div style={{ fontSize: '0.72rem', color: '#8794a5' }}>{r.county}</div>}
                            </td>
                            <td style={td}>
                                <div>{r.document_no ?? '-'}</div>
                                {r.fiscal_no && <div style={{ fontSize: '0.72rem', color: '#8794a5' }}>fiskal: {r.fiscal_no}</div>}
                            </td>
                            <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {r.quantity_total === null ? '-' : r.quantity_total.toLocaleString('hr-HR', { minimumFractionDigits: 3, maximumFractionDigits: 4 })}
                            </td>
                            <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 700 }}>{money(r.amount_eur)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/** Panel na predmetu: povijest stranke iz Faros MUP evidencije. */
export function BuybackHistoryPanel({ caseId, token }: { caseId: number; token: string }) {
    const [data, setData] = useState<BuybackHistory | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError(null);
        api<BuybackHistory>(`/kyc/cases/${caseId}/buyback-history`, token)
            .then((r) => { if (alive) setData(r); })
            .catch((e: Error) => { if (alive) setError(e.message); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [caseId, token]);

    if (loading) return <div style={card}>Ucitavam povijest otkupa…</div>;
    if (error) return <div style={{ ...card, color: '#bb1111' }}>Povijest otkupa nije dostupna: {error}</div>;
    if (!data) return null;

    if (!data.available) {
        return (
            <div style={card}>
                <Head title="Povijest otkupa (MUP evidencija)" />
                <div style={{ fontSize: '0.84rem', color: '#8794a5' }}>
                    Evidencija jos nije uvezena. Stize kao Faros izvjestaj na obrada@aurodomus.eu.
                </div>
            </div>
        );
    }

    if (!data.found) {
        return (
            <div style={card}>
                <Head title="Povijest otkupa (MUP evidencija)" />
                <div style={{ fontSize: '0.84rem', color: '#4a6785' }}>
                    {data.reason === 'no_oib'
                        ? 'Upisite OIB stranke da bi se povukla povijest transakcija.'
                        : 'Nema evidentiranih transakcija za ovaj OIB. Stranka je nova.'}
                </div>
            </div>
        );
    }

    const s = data.summary as Summary;
    const shown = expanded ? data.rows : data.rows.slice(0, 8);

    return (
        <div style={card}>
            <Head
                title="Povijest otkupa (MUP evidencija)"
                note={`${s.count} transakcija${s.first_date ? `, od ${s.first_date} do ${s.last_date}` : ''}${data.masked ? ' - podaci maskirani' : ''}`}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, marginTop: 12 }}>
                <Stat label="Ukupno" value={money(s.amount_eur)} note={`${s.count} transakcija`} />
                <Stat label="Najveca pojedinacna" value={money(s.max_single_eur)} />
                <Stat label="Zadnjih 12 mjeseci" value={money(s.rolling_12m_eur)} />
                <Stat label="Kolicina" value={s.quantity_total.toLocaleString('hr-HR', { maximumFractionDigits: 3 })} note="zlato + srebro" />
                {s.branches[0] && <Stat label="Najcesca poslovnica" value={s.branches[0].branch} note={`${s.branches[0].count}x`} />}
            </div>

            <FlagList flags={data.flags} />

            {data.linked_groups.length > 0 && (
                <div style={{ marginTop: 12 }}>
                    <MiniTitle>Povezane transakcije</MiniTitle>
                    {data.linked_groups.map((g) => (
                        <div key={`${g.from}-${g.to}`} style={{ fontSize: '0.8rem', color: '#33404f', padding: '5px 0', borderBottom: '1px solid #eef3f8' }}>
                            {g.from} - {g.to}: {g.count} transakcija, ukupno <strong>{money(g.amount_eur)}</strong>
                            {g.documents.length > 0 && <span style={{ color: '#8794a5' }}> ({g.documents.join(', ')})</span>}
                        </div>
                    ))}
                </div>
            )}

            {data.by_year.length > 1 && (
                <div style={{ marginTop: 12 }}>
                    <MiniTitle>Po godinama</MiniTitle>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                        {data.by_year.map((y) => (
                            <div key={y.year} style={{ fontSize: '0.8rem' }}>
                                <span style={{ fontWeight: 800, color: INK }}>{y.year}</span>
                                <span style={{ color: '#8794a5' }}> · {y.count}x · {money(y.amount_eur)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <MiniTitle>Transakcije</MiniTitle>
            <RowTable rows={shown} showName={false} />
            {data.rows.length > 8 && (
                <button type="button" style={{ ...BTN, marginTop: 8, padding: '6px 10px', fontSize: '0.78rem' }} onClick={() => setExpanded((v) => !v)}>
                    {expanded ? 'Prikazi manje' : `Prikazi svih ${data.rows.length}`}
                </button>
            )}
            {s.rows_truncated && (
                <div style={{ fontSize: '0.74rem', color: '#8794a5', marginTop: 6 }}>
                    Prikazano je prvih {s.rows_shown} od {s.count} transakcija; zbrojevi su racunati nad svima.
                </div>
            )}
        </div>
    );
}

function Head({ title, note }: { title: string; note?: string }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: INK, background: '#eef3f8', borderRadius: 8, padding: 6, display: 'inline-flex' }}><Icon name="cash" size={17} /></span>
            <div>
                <div style={{ fontSize: 15, fontWeight: 780, color: INK, letterSpacing: '-.015em' }}>{title}</div>
                {note && <div style={{ fontSize: 10.8, color: '#8794a5', marginTop: 1 }}>{note}</div>}
            </div>
        </div>
    );
}

function MiniTitle({ children }: { children: React.ReactNode }) {
    return <div style={{ marginTop: 14, marginBottom: 5, color: '#4a6785', fontWeight: 800, fontSize: '0.78rem', textTransform: 'uppercase' }}>{children}</div>;
}

type RegisterResponse = {
    rows: BuybackRow[];
    total: number;
    truncated: boolean;
    masked?: boolean;
    reason?: string;
    facets: { companies: string[]; types: string[]; branches: string[] };
};

const EMPTY_FILTERS = { oib: '', name: '', company: '', type: '', branch: '', from: '', to: '', min_amount: '' };

/** Pretraga cijele MUP evidencije. Zamjenjuje rucno kopanje po Farosu. */
export function BuybackRegister({ token }: { token: string }) {
    const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
    const [data, setData] = useState<RegisterResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const set = (key: keyof typeof EMPTY_FILTERS) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
        setFilters((f) => ({ ...f, [key]: e.target.value }));

    const hasFilter = useMemo(() => Object.values(filters).some((v) => v.trim() !== ''), [filters]);

    const search = useCallback(() => {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => { if (v.trim() !== '') params.set(k, v.trim()); });
        api<RegisterResponse>(`/kyc/buyback-register?${params.toString()}`, token)
            .then(setData)
            .catch((e: Error) => setError(e.message))
            .finally(() => setLoading(false));
    }, [filters, token]);

    // Sifrarnici za padajuce liste stizu s prvim praznim pozivom.
    useEffect(() => {
        api<RegisterResponse>('/kyc/buyback-register', token).then(setData).catch(() => undefined);
    }, [token]);

    const facets = data?.facets ?? { companies: [], types: [], branches: [] };
    const totals = useMemo(() => {
        const rows = data?.rows ?? [];
        return {
            amount: rows.reduce((a, r) => a + (r.amount_eur ?? 0), 0),
            quantity: rows.reduce((a, r) => a + (r.quantity_total ?? 0), 0),
        };
    }, [data]);

    return (
        <div style={card}>
            <Head title="Evidencija otkupa - pretraga" note="Faros izvjestaj Otkup po imenu i prezimenu za MUP" />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginTop: 12 }}>
                <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                    OIB
                    <input style={INP} value={filters.oib} onChange={set('oib')} placeholder="11 znamenki" />
                </label>
                <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                    Ime i prezime
                    <input style={INP} value={filters.name} onChange={set('name')} placeholder="dio imena" />
                </label>
                <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                    Drustvo
                    <select style={INP} value={filters.company} onChange={set('company')}>
                        <option value="">sva</option>
                        {facets.companies.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </label>
                <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                    Vrsta
                    <select style={INP} value={filters.type} onChange={set('type')}>
                        <option value="">sve</option>
                        {facets.types.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
                    </select>
                </label>
                <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                    Poslovnica
                    <input style={INP} value={filters.branch} onChange={set('branch')} list="buyback-branches" placeholder="pocetak naziva" />
                    <datalist id="buyback-branches">{facets.branches.map((b) => <option key={b} value={b} />)}</datalist>
                </label>
                <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                    Od datuma
                    <input style={INP} type="date" value={filters.from} onChange={set('from')} />
                </label>
                <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                    Do datuma
                    <input style={INP} type="date" value={filters.to} onChange={set('to')} />
                </label>
                <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                    Iznos od (EUR)
                    <input style={INP} type="number" min="0" step="100" value={filters.min_amount} onChange={set('min_amount')} />
                </label>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
                <button type="button" style={BTN} onClick={search} disabled={!hasFilter || loading}>
                    {loading ? 'Trazim…' : 'Trazi'}
                </button>
                <button
                    type="button"
                    style={{ ...BTN, background: '#eef3f8', color: INK }}
                    onClick={() => { setFilters({ ...EMPTY_FILTERS }); setData((d) => (d ? { ...d, rows: [], total: 0, truncated: false } : d)); }}
                >
                    Ocisti
                </button>
                {!hasFilter && <span style={{ fontSize: '0.76rem', color: '#8794a5' }}>Postavite barem jedan filtar.</span>}
            </div>

            {error && <div style={{ color: '#bb1111', marginTop: 10, fontSize: '0.84rem' }}>{error}</div>}

            {data && data.rows.length > 0 && (
                <>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, marginTop: 14 }}>
                        <Stat label="Redaka" value={String(data.total)} />
                        <Stat label="Zbroj" value={money(totals.amount)} />
                        <Stat label="Kolicina" value={totals.quantity.toLocaleString('hr-HR', { maximumFractionDigits: 3 })} />
                    </div>
                    {data.truncated && (
                        <div style={{ fontSize: '0.76rem', color: '#8a6817', marginTop: 6 }}>
                            Rezultat je odrezan. Suzite razdoblje ili dodajte filtar.
                        </div>
                    )}
                    {data.masked && (
                        <div style={{ fontSize: '0.76rem', color: '#8794a5', marginTop: 6 }}>
                            Imena su maskirana; puni podaci dostupni su ovlasteniku za SPNFT.
                        </div>
                    )}
                    <RowTable rows={data.rows} showName />
                </>
            )}

            {data && data.rows.length === 0 && hasFilter && !loading && !error && (
                <div style={{ marginTop: 12, fontSize: '0.84rem', color: '#8794a5' }}>Nema rezultata za zadane filtre.</div>
            )}
        </div>
    );
}
